// The regulatory drift read the admin SPA makes, on the `HttpClient` with the browser session
// cookie: one ecosystem's snapshot, decoded with its closed admin contract. A refusal is kept as
// the reason the screen explains, with the error code and trace id its body names to quote to
// support, never retried as a throw.
import { Effect, Function, Option, Schema } from 'effect';
import { FetchHttpClient, HttpClient, HttpClientError, HttpClientRequest } from 'effect/http';
import { RegulatoryEndpointSnapshot } from '@finnest/power-admin-contracts';
import { PowerRefusal, powerRefusalOf } from '@finnest/power-admin-contracts/failure';
import type { RegulatoryEcosystem } from '@finnest/power-admin-contracts';

/** Why the read did not succeed, as the screen explains it. */
export const DriftFailureReason = Schema.Literals([
  'unauthenticated',
  'not-served',
  'unavailable',
  'invalid',
  'failed',
]);
export type DriftFailureReason = typeof DriftFailureReason.Type;

/**
 * What failed under the reason: the transport or body read, or the snapshot decode. A refusal
 * Power answered carries none; its status is the reason.
 */
const DriftFailureCause = Schema.Union([
  Schema.instanceOf(HttpClientError.HttpClientError),
  Schema.instanceOf(Schema.SchemaError),
]);

export class DriftRequestFailed extends Schema.TaggedError<DriftRequestFailed>()(
  'DriftRequestFailed',
  {
    reason: DriftFailureReason,
    cause: Schema.optionalKey(DriftFailureCause),
    /** The error code and trace id the answer names, to quote to support; none without an answer. */
    refusal: Schema.NullOr(PowerRefusal),
  },
) {
  override get message(): string {
    return `regulatory drift request failed: ${this.reason}`;
  }
}

/** One drift read of this deployment's Power API. */
export type DriftRead<A> = Effect.Effect<A, DriftRequestFailed, HttpClient.HttpClient>;

const DRIFT_PATH = '/power/admin/regulatory-drift';
const HTTP_OK = 200;
const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_NOT_FOUND = 404;
const HTTP_UNAVAILABLE = 503;
const CLOSED = { onExcessProperty: 'error' } as const;

const REASON_BY_STATUS: ReadonlyMap<number, DriftFailureReason> = new Map([
  [HTTP_BAD_REQUEST, 'invalid'],
  [HTTP_UNAUTHORIZED, 'unauthenticated'],
  [HTTP_NOT_FOUND, 'not-served'],
  [HTTP_UNAVAILABLE, 'unavailable'],
]);

const failedBy =
  (reason: DriftFailureReason) =>
  (cause: typeof DriftFailureCause.Type): DriftRequestFailed =>
    DriftRequestFailed.make({ reason, cause, refusal: null });

/** The error code and trace id a failed read names, to quote to support; none without an answer. */
export const failureRefusal = (failure: DriftRequestFailed): Option.Option<PowerRefusal> =>
  Option.fromNullOr(failure.refusal);

const decodeSnapshot = Schema.decodeEffect(Schema.fromJsonString(RegulatoryEndpointSnapshot));

const readAt = (
  apiBaseUrl: string,
  ecosystem: RegulatoryEcosystem,
): DriftRead<RegulatoryEndpointSnapshot> =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client
      .execute(HttpClientRequest.get(`${apiBaseUrl}${DRIFT_PATH}/${ecosystem}`))
      .pipe(Effect.mapError(failedBy('failed')));
    if (response.status !== HTTP_OK) {
      // The status gives the reason; a body that cannot be read only loses the reference.
      const reason = REASON_BY_STATUS.get(response.status) ?? 'failed';
      const text = yield* response.text.pipe(Effect.mapError(failedBy(reason)));
      return yield* DriftRequestFailed.make({
        reason,
        refusal: Option.getOrNull(powerRefusalOf(text)),
      });
    }
    const text = yield* response.text.pipe(Effect.mapError(failedBy('failed')));
    return yield* decodeSnapshot(text, CLOSED).pipe(Effect.mapError(failedBy('invalid')));
  }).pipe(
    Effect.provideService(FetchHttpClient.RequestInit, { credentials: 'include' }),
    Effect.provideService(HttpClient.TracerPropagationEnabled, false),
  );

/** The `ecosystem` snapshot of the Power API at `apiBaseUrl`. */
export const readDriftSnapshot: {
  (ecosystem: RegulatoryEcosystem): (apiBaseUrl: string) => DriftRead<RegulatoryEndpointSnapshot>;
  (apiBaseUrl: string, ecosystem: RegulatoryEcosystem): DriftRead<RegulatoryEndpointSnapshot>;
} = Function.dual(2, readAt);
